import React from 'react';
import { supabase } from '@/lib/supabase';
import { Plus, Edit, Trash2, GripVertical } from 'lucide-react';
import type { Database } from '@/lib/database.types';

type FAQ = Database['public']['Tables']['faqs']['Row'];

export function FAQs() {
  const [faqs, setFaqs] = React.useState<FAQ[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [editingFaq, setEditingFaq] = React.useState<FAQ | null>(null);
  const [formData, setFormData] = React.useState({
    question: '',
    answer: ''
  });

  React.useEffect(() => {
    fetchFaqs();
  }, []);

  async function fetchFaqs() {
    try {
      const { data, error } = await supabase
        .from('faqs')
        .select('*')
        .order('order_index');

      if (error) throw error;
      setFaqs(data || []);
    } catch (err) {
      console.error('Error fetching FAQs:', err);
      setError('Failed to load FAQs');
    } finally {
      setLoading(false);
    }
  }

  function handleEdit(faq: FAQ) {
    setEditingFaq(faq);
    setFormData({
      question: faq.question,
      answer: faq.answer
    });
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    try {
      if (editingFaq) {
        const { error } = await supabase
          .from('faqs')
          .update({
            question: formData.question,
            answer: formData.answer
          })
          .eq('id', editingFaq.id);

        if (error) throw error;
      } else {
        const maxOrderIndex = Math.max(...faqs.map(f => f.order_index), -1);
        const { error } = await supabase
          .from('faqs')
          .insert({
            question: formData.question,
            answer: formData.answer,
            order_index: maxOrderIndex + 1
          });

        if (error) throw error;
      }

      setFormData({ question: '', answer: '' });
      setEditingFaq(null);
      fetchFaqs();
    } catch (err) {
      console.error('Error saving FAQ:', err);
      setError('Failed to save FAQ');
    }
  }

  async function handleDelete(id: string) {
    if (!window.confirm('Are you sure you want to delete this FAQ?')) {
      return;
    }

    try {
      const { error } = await supabase
        .from('faqs')
        .delete()
        .eq('id', id);

      if (error) throw error;
      fetchFaqs();
    } catch (err) {
      console.error('Error deleting FAQ:', err);
      setError('Failed to delete FAQ');
    }
  }

  async function handleReorder(fromIndex: number, toIndex: number) {
    const newFaqs = [...faqs];
    const [movedFaq] = newFaqs.splice(fromIndex, 1);
    newFaqs.splice(toIndex, 0, movedFaq);

    // Update order_index for all FAQs
    const updates = newFaqs.map((faq, index) => ({
      id: faq.id,
      order_index: index
    }));

    try {
      const { error } = await supabase
        .from('faqs')
        .upsert(updates);

      if (error) throw error;
      setFaqs(newFaqs);
    } catch (err) {
      console.error('Error reordering FAQs:', err);
      setError('Failed to reorder FAQs');
      fetchFaqs(); // Reload original order
    }
  }

  if (loading) {
    return (
      <div className="animate-pulse">
        <div className="h-8 bg-gray-200 rounded w-1/4 mb-8" />
        <div className="space-y-4">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="bg-white p-4 shadow rounded-lg">
              <div className="h-4 bg-gray-200 rounded w-3/4" />
              <div className="mt-4 h-4 bg-gray-200 rounded w-1/2" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div>
      <h1 className="text-2xl font-semibold text-gray-900">FAQs</h1>

      <div className="mt-8 max-w-3xl">
        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="rounded-md bg-red-50 p-4">
              <div className="text-sm text-red-700">{error}</div>
            </div>
          )}

          <div>
            <label htmlFor="question" className="block text-sm font-medium text-gray-700">
              Question
            </label>
            <input
              type="text"
              id="question"
              required
              value={formData.question}
              onChange={(e) => setFormData({ ...formData, question: e.target.value })}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm"
            />
          </div>

          <div>
            <label htmlFor="answer" className="block text-sm font-medium text-gray-700">
              Answer
            </label>
            <textarea
              id="answer"
              required
              rows={4}
              value={formData.answer}
              onChange={(e) => setFormData({ ...formData, answer: e.target.value })}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm"
            />
          </div>

          <div>
            <button
              type="submit"
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
            >
              {editingFaq ? (
                'Update FAQ'
              ) : (
                <>
                  <Plus className="h-4 w-4 mr-2" />
                  Add FAQ
                </>
              )}
            </button>
            {editingFaq && (
              <button
                type="button"
                onClick={() => {
                  setEditingFaq(null);
                  setFormData({ question: '', answer: '' });
                }}
                className="ml-3 inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
              >
                Cancel
              </button>
            )}
          </div>
        </form>
      </div>

      <div className="mt-8">
        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <div
              key={faq.id}
              className="bg-white shadow rounded-lg overflow-hidden"
            >
              <div className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h3 className="text-lg font-medium text-gray-900">
                      {faq.question}
                    </h3>
                    <p className="mt-2 text-gray-500">{faq.answer}</p>
                  </div>
                  <div className="ml-4 flex-shrink-0 flex items-center space-x-4">
                    <button
                      className="text-gray-400 hover:text-gray-500 cursor-move"
                      onMouseDown={(e) => {
                        const startY = e.pageY;
                        const startIndex = index;
                        
                        function onMouseMove(e: MouseEvent) {
                          const currentY = e.pageY;
                          const diff = currentY - startY;
                          const newIndex = Math.max(0, Math.min(
                            startIndex + Math.round(diff / 50),
                            faqs.length - 1
                          ));
                          
                          if (newIndex !== startIndex) {
                            handleReorder(startIndex, newIndex);
                          }
                        }
                        
                        function onMouseUp() {
                          document.removeEventListener('mousemove', onMouseMove);
                          document.removeEventListener('mouseup', onMouseUp);
                        }
                        
                        document.addEventListener('mousemove', onMouseMove);
                        document.addEventListener('mouseup', onMouseUp);
                      }}
                    >
                      <GripVertical className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleEdit(faq)}
                      className="text-gray-400 hover:text-gray-500"
                    >
                      <Edit className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(faq.id)}
                      className="text-red-400 hover:text-red-500"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}